import { useEffect, useMemo, useRef, useState } from 'react';
import {
  isEntryEditable,
  validateCyclingEntry,
  type DuelSide,
  type Entry,
  type Fixture,
  type Round
} from '@h2h/shared';
import { useSubmitCyclingEntry } from '../../hooks/useQueries';
import { toDuelPicks, CYCLING_WEIGHTS } from '../../lib/cyclingPicks';
import { useHaptics } from '../../hooks/useHaptics';
import type { LadderRungData } from '../../components/play/cycling-ui';
import { TOTAL } from './constants';

export type CyclingPickStep = 'calls' | 'rank';

/**
 * Draft state for the cycling pick sheet on the League Hub — mirrors
 * useMakePicksDraft for football: seed from entry, autosave, duel deep-link focus.
 */
export function useCyclingPicksDraft({
  leagueId,
  roundId,
  round,
  fixtures,
  entry,
  uid,
  now,
  duelFocus,
  openToStep
}: {
  leagueId: string;
  roundId: string;
  round: Round;
  fixtures: Fixture[] | undefined;
  entry: Entry | null | undefined;
  uid: string | null;
  now: number;
  /** Deep-link: scroll to this duel card on the calls step. */
  duelFocus?: string | null;
  /**
   * Step to land on when seeding an already-complete entry. Defaults to 'rank'
   * (legacy). Pass 'calls' when the flow is opened to edit picks (the hub now
   * owns inline ranking, so re-entry is for changing a rider).
   */
  openToStep?: CyclingPickStep;
}) {
  const { impact } = useHaptics();
  const submitEntry = useSubmitCyclingEntry(uid);
  const lastSavedRef = useRef('');
  const wasAllPicked = useRef(false);

  const slate = useMemo(
    () => [...(fixtures ?? [])].sort((a, b) => a.fixtureId.localeCompare(b.fixtureId)),
    [fixtures]
  );
  const fixtureById = useMemo(() => {
    const m = new Map<string, Fixture>();
    for (const f of slate) m.set(f.fixtureId, f);
    return m;
  }, [slate]);

  const slateReady = slate.length === TOTAL;
  const frozen = !isEntryEditable(round, [], now);
  const picksOpen = round.status === 'OPEN' || round.status === 'LOCKING';

  const [sideByDuel, setSideByDuel] = useState<Record<string, DuelSide | undefined>>({});
  const [order, setOrder] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [pickStep, setPickStep] = useState<CyclingPickStep>('calls');
  const [error, setError] = useState<string | null>(null);

  const pageLoading = !seeded || fixtures == null || entry === undefined;

  useEffect(() => {
    setSeeded(false);
    setSideByDuel({});
    setOrder([]);
    setPickStep('calls');
    wasAllPicked.current = false;
  }, [roundId]);

  useEffect(() => {
    if (!duelFocus || pageLoading || pickStep !== 'calls') return;
    const id = window.setTimeout(() => {
      document.getElementById(`duel-card-${duelFocus}`)?.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 360);
    return () => window.clearTimeout(id);
  }, [duelFocus, pageLoading, pickStep]);

  useEffect(() => {
    if (seeded || fixtures == null || entry === undefined || !slateReady) return;
    if (entry?.duelPicks?.length) {
      const ranked = [...entry.duelPicks].sort((a, b) => b.confidenceWeight - a.confidenceWeight);
      setOrder(ranked.map((p) => p.duelId));
      setSideByDuel(Object.fromEntries(ranked.map((p) => [p.duelId, p.pick])));
      if (entry.duelPicks.length === TOTAL) {
        setPickStep(openToStep ?? 'rank');
        wasAllPicked.current = true;
      }
    } else {
      setOrder(slate.map((f) => f.fixtureId));
      setSideByDuel({});
    }
    setSeeded(true);
  }, [seeded, fixtures, entry, slate, slateReady, openToStep]);

  const pickedCount = order.filter((id) => !!sideByDuel[id]).length;
  const allPicked = slateReady && pickedCount === TOTAL;

  const duelPicks = useMemo(() => {
    if (!allPicked) return null;
    return toDuelPicks(order.map((id) => ({ duelId: id, pick: sideByDuel[id] })), CYCLING_WEIGHTS);
  }, [allPicked, order, sideByDuel]);

  const valid = duelPicks != null && validateCyclingEntry(duelPicks, round).ok;

  const flowStep: CyclingPickStep = pickStep === 'rank' && allPicked ? 'rank' : 'calls';

  useEffect(() => {
    if (pageLoading || frozen) return;
    const prev = wasAllPicked.current;
    wasAllPicked.current = allPicked;
    if (allPicked && !prev && pickStep !== 'rank') {
      setPickStep('rank');
    }
  }, [allPicked, pageLoading, frozen, pickStep]);

  const rungs: LadderRungData[] = useMemo(() => {
    return order
      .filter((id) => sideByDuel[id])
      .map((id) => ({
        duelId: id,
        pick: sideByDuel[id]!,
        fixture: fixtureById.get(id)!
      }))
      .filter((r) => r.fixture);
  }, [order, sideByDuel, fixtureById]);

  const setSide = (duelId: string, side: DuelSide) => {
    if (frozen) return;
    setSideByDuel((cur) => ({ ...cur, [duelId]: side }));
  };

  const reorderRungs = (next: LadderRungData[]) => {
    if (frozen) return;
    setOrder(next.map((r) => r.duelId));
  };

  useEffect(() => {
    if (!leagueId || !roundId || !valid || !duelPicks || frozen) return;
    const sig = JSON.stringify(duelPicks);
    if (sig === lastSavedRef.current) return;
    const id = window.setTimeout(() => {
      lastSavedRef.current = sig;
      submitEntry.mutate(
        { leagueId, roundId, duelPicks },
        { onError: () => { if (lastSavedRef.current === sig) lastSavedRef.current = ''; } }
      );
    }, 500);
    return () => window.clearTimeout(id);
  }, [duelPicks, valid, frozen, leagueId, roundId, submitEntry]);

  const goToCalls = () => {
    impact('light');
    setPickStep('calls');
  };

  const goToRank = () => {
    impact('medium');
    setPickStep('rank');
  };

  return {
    slate,
    slateReady,
    frozen,
    picksOpen,
    pageLoading,
    flowStep,
    sideByDuel,
    pickedCount,
    allPicked,
    rungs,
    valid,
    error,
    setError,
    setSide,
    reorderRungs,
    goToCalls,
    goToRank,
    duelPicks,
    submitEntry,
    lastSavedRef,
    lockAt: round.firstKickoffAt,
    routeLabel: round.label?.replace(/^Stage \d+\s*/i, '') || undefined,
    stageLabel: round.label ?? 'Stage'
  };
}
